import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { isSafeNext, withNext } from './postAuthRedirect';
import { useAuth } from './useAuth';

export default function RequireAuth({ children }: { children: ReactNode }) {
  const { status } = useAuth();
  const location = useLocation();

  if (status === 'initializing') {
    // Avoid flicker; AuthProvider resolves on first effect.
    return null;
  }
  if (status !== 'authenticated') {
    // The target has to travel in `?next=`, NOT in router state: SignIn
    // resolves its post-auth destination with `readNext(location.search)` and
    // never reads `location.state`. Passing state alone silently dropped every
    // deep link into a gated route at the /app chooser — which is the entire
    // job of the /app/documents link an agent SMS sends, since a text is
    // almost always opened on a phone that is not signed in.
    //
    // Search is included so query-carrying targets survive; `isSafeNext`
    // re-runs the open-redirect guard here so a malformed target degrades to
    // the plain sign-in page rather than a bad `?next=`.
    const target = `${location.pathname}${location.search}`;
    return (
      <Navigate
        to={isSafeNext(target) ? withNext('/auth/signin', target) : '/auth/signin'}
        replace
        state={{ from: location }}
      />
    );
  }
  return <>{children}</>;
}
